import React, { useState, useRef } from 'react';
import { graphql } from 'gatsby';
import Img from 'gatsby-image';
import { FaAngleLeft } from 'react-icons/fa';
import { withPreview } from 'gatsby-source-prismic';
import { HTMLContent } from '../components/Content';
import LocalizedLink from '../components/LocalizedLink';
import siteMetadata from '../siteMetadata';
import Button from '../components/Button';
import PricePill from '../components/PricePill';
import SEO from '../components/SEO';

const ServiceDetailTemplate = ({ headline, parentServiceGroup, featuredimage, prices, details = [], lang }) => {
  const contentRef = useRef(null);
  const [selectedItem, setSelectedItem] = useState(details[0]);

  const isSelectedDetail = detail => detail && selectedItem && detail.name === selectedItem.name;

  const setSelectedDetail = detail => {
    setSelectedItem(detail);
    // eslint-disable-next-line
    window.scrollTo(0, contentRef.current.offsetTop - 65);
  };

  return (
    <div className="container mx-auto flex pt-2 pb-4">
      <div className="flex flex-col w-full xl:flex-row xl:space-x-6">
        <div className="xl:w-1/2">
          <div className="xl:sticky xl:top-22">
            {parentServiceGroup && (
              <LocalizedLink
                className="flex px-4 items-center underline text-light-500 mb-2 capitalize font-bold text-sm xl:mb-4"
                to={`services/${parentServiceGroup.uid}`}
              >
                <FaAngleLeft />
                {lang === siteMetadata.defaultLang
                  ? `Back to ${parentServiceGroup.data.headline}`
                  : `Volver a ${parentServiceGroup.data.headline}`}
              </LocalizedLink>
            )}

            {featuredimage && (
              <div className="xl:px-4">
                <Img
                  className="flex justify-center w-full shadow-lg"
                  fluid={featuredimage.fluid}
                  alt={featuredimage.alt}
                />
              </div>
            )}
            <div className="hidden px-4 pt-6 xl:flex justify-center w-full">
              <Button
                to="contact"
                className="w-full text-center xl:w-4/5"
                label={lang === siteMetadata.defaultLang ? "I'm interested!" : 'Me interesa!'}
              />
            </div>
          </div>
        </div>
        <div className="flex flex-col xl:w-1/2" ref={contentRef}>
          <div className="flex flex-col sticky top-16 bg-white lg:pt-6 lg:pb-4 xl:top-18">
            {headline && (
              <h1 className="flex justify-center px-4 pt-4 text-4xl text-light-500 leading-none w-full lg:text-5xl text-center mb-4">
                {headline}
              </h1>
            )}
            <PricePill prices={prices} lang={lang} className="mb-4" />
            <div className="flex justify-center relative xl:px-4">
              <div
                className={`flex shadow space-x-2 px-4 w-full lg:w-3/4 xl:w-full ${
                  details.length > 1 ? 'justify-between' : 'justify-center'
                }`}
              >
                {details.map(detail => (
                  <button
                    type="button"
                    onClick={() => setSelectedDetail(detail)}
                    className={`text-center text-sm capitalize py-3 relative focus:outline-none xl:text-base ${
                      isSelectedDetail(detail) ? 'font-black' : 'text-gray-700'
                    }`}
                  >
                    {detail.name}
                    {isSelectedDetail(detail) && (
                      <div className="absolute bottom-0 flex justify-center w-full">
                        <div className="border border-light-500 border-t-2 w-2/3" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {selectedItem && (
            <div className="flex justify-center">
              <HTMLContent
                className="service-detail px-4 pt-4 mb-0 w-full lg:w-3/4 xl:w-full"
                content={selectedItem.body.html}
              />
            </div>
          )}
        </div>
        <div className="flex px-4 sticky bottom-4 mt-8 xl:hidden justify-center">
          <Button
            to="contact"
            className="w-full text-center lg:w-4/5"
            label={lang === siteMetadata.defaultLang ? "I'm interested!" : 'Me interesa!'}
          />
        </div>
      </div>
    </div>
  );
};

const ServiceDetail = ({ data, pageContext: { lang, shortLang }, location: { pathname } }) => {
  const {
    prismicService: {
      data: { headline, subheadline, featuredimage, metaImage, prices, parent, details },
    },
  } = data;

  const parentServiceGroup = parent && parent.document;
  const serviceDetails = details.map(detail => ({ name: detail.name, body: detail.body_details }));

  return (
    <>
      <SEO title={headline} description={subheadline} image={metaImage} pathname={pathname} lang={shortLang} />
      <ServiceDetailTemplate
        headline={headline}
        parentServiceGroup={parentServiceGroup}
        featuredimage={featuredimage}
        prices={prices}
        details={serviceDetails}
        lang={lang}
      />
    </>
  );
};

export const pageQuery = graphql`
  query ServiceDetailTemplate($prismicId: ID!, $lang: String, $shortLang: String) {
    prismicService(prismicId: { eq: $prismicId }) {
      data {
        headline
        subheadline
        metaImage: featuredimage {
          fluid(imgixParams: { ar: "16:10", fit: "crop" }, maxWidth: 1200) {
            ...GatsbyPrismicImageFluid
          }
        }
        featuredimage {
          fluid(imgixParams: { ar: "16:10", fit: "crop" }, maxWidth: 512) {
            ...GatsbyPrismicImageFluid
          }
        }
        prices {
          amount
          currency
        }
        details {
          body_details {
            html
          }
          name
        }
        parent {
          document {
            ... on PrismicServiceGroup {
              id
              data {
                headline
              }
              uid
            }
          }
        }
      }
    }
    prismicFooter(lang: { eq: $lang }) {
      ...FooterFragment
    }
    prismicDisclaimer(lang: { eq: $lang }) {
      ...PostDisclaimerFragment
    }
    prismicWhyHireMe(lang: { eq: $lang }) {
      ...WhyHireMeFragment
    }
    googleReviews: allGoogleReview(
      sort: { fields: position, order: ASC }
      limit: 3
      filter: { lang: { eq: $shortLang } }
    ) {
      edges {
        node {
          ...GoogleReviewsFragment
        }
      }
    }
  }
`;

export default withPreview(ServiceDetail);
